import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";

import { listEmployersApi, DpEmployer } from "../api/settingsApi";
import {
  createFgtsPaymentApi,
  deleteFgtsPaymentApi,
  listFgtsPaymentsApi,
  updateFgtsPaymentApi,
  DpFgtsPayment,
  DpFgtsPaymentList,
} from "../api/fgtsApi";
import {
  DpFgtsDocumentType,
  FGTS_DOCUMENT_TYPE_LABELS,
  FGTS_PAYMENT_STATUS_BADGE,
  FGTS_PAYMENT_STATUS_LABELS,
} from "../api/dpTypes";
import {
  AttachmentDraft,
  EMPTY_ATTACHMENT_DRAFT,
  PaymentAttachmentFields,
  PaymentFileButtons,
  resolveAttachments,
} from "../components/PaymentAttachments";
import RecolhimentosTabs from "../components/RecolhimentosTabs";
import { formatCurrency, formatDateOnly, toDateInputValue } from "../utils/format";
import { DP_PAGE, DP_SETTINGS_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";
import { GuideFinanceCell } from "../components/FinanceBridgeBox";

const MONTH_LABELS = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const pad2 = (n: number) => String(n).padStart(2, "0");
const competenceLabel = (year: number, month: number) => `${pad2(month)}/${year}`;

/** Hoje como 'YYYY-MM-DD' no fuso local — é o limite do `<input type="date">`. */
const todayInputValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
};

/** O típico é lançar a competência do mês anterior (a guia vence no dia 20 do mês seguinte). */
const previousCompetence = () => {
  const now = new Date();
  const month = now.getMonth(); // 0-based: o mês anterior em 1–12
  return month === 0
    ? { year: now.getFullYear() - 1, month: 12 }
    : { year: now.getFullYear(), month };
};

const emptyForm = () => {
  const { year, month } = previousCompetence();
  return {
    year: String(year),
    month: String(month),
    fgtsAmount: "",
    consignadoAmount: "",
    documentType: "GUIA_UNICA" as DpFgtsDocumentType,
    paidAt: "",
    notes: "",
  };
};

const EMPTY_LIST: DpFgtsPaymentList = {
  items: [],
  totals: { fgts: "0", consignado: "0", total: "0" },
};

export default function FgtsPaymentsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () => Array.from({ length: currentYear - 2020 + 1 }, (_, i) => currentYear - i),
    [currentYear]
  );

  const [employers, setEmployers] = useState<DpEmployer[]>([]);
  const [employersLoaded, setEmployersLoaded] = useState(false);
  const [employerId, setEmployerId] = useState("");
  const [year, setYear] = useState(currentYear);

  const [list, setList] = useState<DpFgtsPaymentList>(EMPTY_LIST);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [modal, setModal] = useState<{ payment?: DpFgtsPayment } | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState<AttachmentDraft>(EMPTY_ATTACHMENT_DRAFT);
  const [removing, setRemoving] = useState<DpFgtsPayment | null>(null);
  const [deleting, setDeleting] = useState(false);

  const activeEmployers = useMemo(() => employers.filter((e) => e.isActive), [employers]);
  const selectedEmployer = activeEmployers.find((e) => e.id === employerId);

  useEffect(() => {
    if (!token) return;
    listEmployersApi(token)
      .then((rows) => {
        setEmployers(rows);
        // Com um único empregador ativo (a Eco), ele é resolvido sem pedir escolha.
        const firstActive = rows.find((e) => e.isActive);
        if (firstActive) setEmployerId(firstActive.id);
      })
      .catch((error: any) => toast.error(error?.message || "Erro ao carregar empregadores."))
      .finally(() => setEmployersLoaded(true));
  }, [token]);

  const load = useCallback(async () => {
    if (!token || !employerId) return;
    setLoading(true);
    try {
      setList(await listFgtsPaymentsApi(token, { employerId, year }));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar recolhimentos.");
    } finally {
      setLoading(false);
    }
  }, [token, employerId, year]);

  useEffect(() => {
    load();
  }, [load]);

  const openModal = (payment?: DpFgtsPayment) => {
    setForm(
      payment
        ? {
            year: String(payment.year),
            month: String(payment.month),
            fgtsAmount: String(Number(payment.fgtsAmount)),
            consignadoAmount: String(Number(payment.consignadoAmount)),
            documentType: payment.documentType,
            paidAt: toDateInputValue(payment.paidAt),
            notes: payment.notes ?? "",
          }
        : emptyForm()
    );
    setFiles(EMPTY_ATTACHMENT_DRAFT);
    setModal({ payment });
  };

  const liveTotal = (Number(form.fgtsAmount) || 0) + (Number(form.consignadoAmount) || 0);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !modal) return;

    const fgtsAmount = Number(form.fgtsAmount);
    const consignadoAmount = form.consignadoAmount.trim() === "" ? 0 : Number(form.consignadoAmount);
    if (!Number.isFinite(fgtsAmount) || fgtsAmount <= 0) {
      toast.error("Informe o valor do FGTS (maior que zero).");
      return;
    }
    if (!Number.isFinite(consignadoAmount) || consignadoAmount < 0) {
      toast.error("O consignado não pode ser negativo.");
      return;
    }

    const common = {
      fgtsAmount,
      consignadoAmount,
      documentType: form.documentType,
      paidAt: form.paidAt || null,
      notes: form.notes.trim() || null,
    };

    setSaving(true);
    try {
      const attachments = await resolveAttachments(token, "fgts-payments", files);
      if (modal.payment) {
        const saved = await updateFgtsPaymentApi(token, modal.payment.id, { ...common, ...attachments });
        toast.success("Recolhimento atualizado.");
        if (saved?.financeiroWarning) toast.warning(saved.financeiroWarning);
      } else {
        const saved = await createFgtsPaymentApi(token, {
          ...common,
          ...attachments,
          employerId,
          year: Number(form.year),
          month: Number(form.month),
        });
        toast.success("Competência lançada.");
        if (saved?.financeiroWarning) toast.warning(saved.financeiroWarning);
      }
      setModal(null);
      // Lançou em outro ano: acompanha o filtro, para o registro aparecer na tabela.
      if (!modal.payment && Number(form.year) !== year) setYear(Number(form.year));
      else await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar recolhimento.");
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    if (!token || !removing) return;
    setDeleting(true);
    try {
      await deleteFgtsPaymentApi(token, removing.id);
      toast.success("Lançamento excluído.");
      setRemoving(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir lançamento.");
    } finally {
      setDeleting(false);
    }
  };

  if (!token) return null;

  const noEmployer = employersLoaded && activeEmployers.length === 0;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[{ label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) }, { label: "Recolhimentos" }]}
            title="Recolhimentos de FGTS"
            subtitle="Controle mensal das guias de FGTS e consignado: quanto foi recolhido e quando."
            onBack={goBack}
            backLabel="Voltar para o DP"
          />

          <RecolhimentosTabs />

          {noEmployer ? (
            <div className="dpNotice dpNotice--warning">
              Nenhum empregador ativo cadastrado. Cadastre o CNPJ em{" "}
              <button
                type="button"
                className="dpBtn dpBtn--secondary dpBtn--small"
                onClick={() => navigate(DP_SETTINGS_PAGE())}
              >
                Cadastros
              </button>{" "}
              antes de lançar recolhimentos.
            </div>
          ) : (
            <>
              <div className="dpControls">
                <select
                  className="dpSelect"
                  aria-label="Ano"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>

                {activeEmployers.length > 1 ? (
                  <select
                    className="dpSelect"
                    aria-label="Empregador"
                    value={employerId}
                    onChange={(e) => setEmployerId(e.target.value)}
                  >
                    {activeEmployers.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.tradeName || e.legalName}
                      </option>
                    ))}
                  </select>
                ) : null}

                <button
                  className="dpBtn dpBtn--primary"
                  onClick={() => openModal()}
                  disabled={!employerId}
                >
                  Lançar competência
                </button>
              </div>

              <p className="dpNotice">
                O vencimento é o dia 20 do mês seguinte à competência (FGTS Digital), sem ajuste
                para fim de semana ou feriado. Sem data de pagamento, a competência fica pendente
                até o vencimento e atrasada depois dele.
              </p>

              {loading ? (
                <div className="dpEmpty">Carregando...</div>
              ) : (
                <div className="dpTableWrapper">
                  <table className="dpTable dpTable--fgts">
                    <thead>
                      <tr>
                        <th>Competência</th>
                        <th>FGTS</th>
                        <th>Consignado</th>
                        <th>Total</th>
                        <th>Vencimento</th>
                        <th>Pago em</th>
                        <th>Documento</th>
                        <th>Situação</th>
                        <th>Financeiro</th>
                        <th style={{ width: 260 }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.items.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="dpEmpty">
                            Nenhuma competência lançada em {year}.
                          </td>
                        </tr>
                      ) : (
                        list.items.map((p) => (
                          <tr key={p.id}>
                            <td>
                              <strong>{competenceLabel(p.year, p.month)}</strong>
                              {p.notes ? <div className="dpFormHint">{p.notes}</div> : null}
                            </td>
                            <td>{formatCurrency(p.fgtsAmount)}</td>
                            <td>{formatCurrency(p.consignadoAmount)}</td>
                            <td>
                              <strong>{formatCurrency(p.totalAmount)}</strong>
                            </td>
                            <td>{formatDateOnly(p.dueDate)}</td>
                            <td>{formatDateOnly(p.paidAt)}</td>
                            <td>{FGTS_DOCUMENT_TYPE_LABELS[p.documentType]}</td>
                            <td>
                              <span className={`dpBadge ${FGTS_PAYMENT_STATUS_BADGE[p.status]}`}>
                                {FGTS_PAYMENT_STATUS_LABELS[p.status]}
                              </span>
                            </td>
                            <td>
                              <GuideFinanceCell finance={p.financeiro} guideTotal={p.totalAmount} warning={p.financeiroWarning} />
                            </td>
                            <td>
                              <div className="dpTable__actions">
                                <PaymentFileButtons token={token} area="fgts-payments" id={p.id} files={p} />
                                <button
                                  className="dpBtn dpBtn--secondary dpBtn--small"
                                  onClick={() => openModal(p)}
                                >
                                  Editar
                                </button>
                                <button
                                  className="dpBtn dpBtn--danger dpBtn--small"
                                  onClick={() => setRemoving(p)}
                                >
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {list.items.length > 0 ? (
                      <tfoot>
                        <tr>
                          <td>Total {year}</td>
                          <td>{formatCurrency(list.totals.fgts)}</td>
                          <td>{formatCurrency(list.totals.consignado)}</td>
                          <td>{formatCurrency(list.totals.total)}</td>
                          <td colSpan={6}></td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* ── Modal: lançar / editar competência ─────────────────────────────── */}
      {modal ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {modal.payment
                  ? `Editar competência ${competenceLabel(modal.payment.year, modal.payment.month)}`
                  : "Lançar competência"}
              </h2>
              <button className="dpModal__close" onClick={() => setModal(null)}>
                &times;
              </button>
            </header>

            <form onSubmit={save}>
              <div className="dpModal__body">
                {activeEmployers.length > 1 && selectedEmployer ? (
                  <p className="dpFormHint" style={{ margin: 0 }}>
                    Empregador: {selectedEmployer.tradeName || selectedEmployer.legalName}
                  </p>
                ) : null}

                <div className="dpFormGrid dpFormGrid--two">
                  <div className="dpFormGroup">
                    <label htmlFor="fgtsMonth">Mês da competência *</label>
                    <select
                      id="fgtsMonth"
                      className="dpSelect"
                      value={form.month}
                      onChange={(e) => setForm((f) => ({ ...f, month: e.target.value }))}
                      disabled={saving || !!modal.payment}
                    >
                      {MONTH_LABELS.map((label, i) => (
                        <option key={label} value={i + 1}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="dpFormGroup">
                    <label htmlFor="fgtsYear">Ano *</label>
                    <select
                      id="fgtsYear"
                      className="dpSelect"
                      value={form.year}
                      onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
                      disabled={saving || !!modal.payment}
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {modal.payment ? (
                  <p className="dpFormHint" style={{ margin: 0 }}>
                    A competência não muda. Se foi lançada errada, exclua e lance de novo.
                  </p>
                ) : null}

                <div className="dpFormGrid dpFormGrid--two">
                  <div className="dpFormGroup">
                    <label htmlFor="fgtsAmount">FGTS (R$) *</label>
                    <input
                      id="fgtsAmount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      className="dpInput"
                      value={form.fgtsAmount}
                      onChange={(e) => setForm((f) => ({ ...f, fgtsAmount: e.target.value }))}
                      disabled={saving}
                    />
                  </div>

                  <div className="dpFormGroup">
                    <label htmlFor="fgtsConsignado">Consignado (R$)</label>
                    <input
                      id="fgtsConsignado"
                      type="number"
                      step="0.01"
                      min="0"
                      className="dpInput"
                      placeholder="0,00"
                      value={form.consignadoAmount}
                      onChange={(e) => setForm((f) => ({ ...f, consignadoAmount: e.target.value }))}
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="dpNotice" style={{ margin: 0 }}>
                  Total pago: <strong>{formatCurrency(liveTotal)}</strong>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="fgtsDocument">Documento *</label>
                  <select
                    id="fgtsDocument"
                    className="dpSelect"
                    value={form.documentType}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, documentType: e.target.value as DpFgtsDocumentType }))
                    }
                    disabled={saving}
                  >
                    {(Object.keys(FGTS_DOCUMENT_TYPE_LABELS) as DpFgtsDocumentType[]).map((t) => (
                      <option key={t} value={t}>
                        {FGTS_DOCUMENT_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="fgtsPaidAt">Data do pagamento</label>
                  <input
                    id="fgtsPaidAt"
                    type="date"
                    className="dpInput"
                    max={todayInputValue()}
                    value={form.paidAt}
                    onChange={(e) => setForm((f) => ({ ...f, paidAt: e.target.value }))}
                    disabled={saving}
                  />
                  <span className="dpFormHint">Deixe vazio se ainda não pagou.</span>
                </div>

                <div className="dpFormGroup">
                  <label htmlFor="fgtsNotes">Observação</label>
                  <textarea
                    id="fgtsNotes"
                    className="dpTextarea"
                    maxLength={1000}
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    disabled={saving}
                  />
                </div>

                <PaymentAttachmentFields idPrefix="fgtsFile" current={modal.payment} draft={files} onChange={setFiles} disabled={saving} />
              </div>

              <footer className="dpModal__footer">
                <button
                  type="button"
                  className="dpBtn dpBtn--secondary"
                  onClick={() => setModal(null)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      <EcoConfirmDialog
        open={!!removing}
        title="Excluir lançamento"
        message={removing ? `Excluir o lançamento da competência ${competenceLabel(removing.year, removing.month)}? Os anexos deixam de aparecer.` : ""}
        confirmLabel="Excluir"
        tone="danger"
        busy={deleting}
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
